import {
    AfterViewInit,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import {
    AbstractControl,
    FormControl,
    UntypedFormBuilder,
    UntypedFormControl
} from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslocoService } from '@jsverse/transloco';
import { ReplaySubject, Subject } from 'rxjs';
import { first, switchMap, take, takeUntil } from 'rxjs/operators';
import { ClientService } from 'src/app/domain/services';
import { GoogleAnalyticsService } from 'src/app/shared/service/google-analytics.service';
import { ClientField } from '../../../domain/models';
import { Selection } from '../../../selection/models';
import { SelectionService } from '../../../selection/services';
import { BackgroundForm } from '../../models';

@Component({
    selector: 'app-background-form',
    templateUrl: './background-form.component.html',
    styleUrls: ['./background-form.component.scss'],
    standalone: false
})
export class BackgroundFormComponent implements OnInit, AfterViewInit {
    background = BackgroundForm.initialize(this._fb);
    floatLabelControl = new UntypedFormControl('auto');
    clientCode = runtimeConfig.clientCode;

    eligibilityCategoryRequired = false;
    eligibilityExplanationRequired = false;

    ethnicitySelections$ = this.selectionService.ethnicitySelections$;
    raceSelections$ = this.selectionService.raceSelections$;
    barriersSelections$ = this.selectionService.barriersSelections$;
    laborForceStatusSelections$ =
        this.selectionService.laborForceStatusSelections$;
    workStatusSelections$ = this.selectionService.workStatusSelections$;
    employmentStatusSelections$ =
        this.selectionService.employmentStatusSelections$;
    employmentStatusAltSelections$ =
        this.selectionService.employmentStatusAltSelections$;
    employmentStatusTerseSelections$ =
        this.selectionService.employmentStatusTerseSelections$;
    registeredWithEmploymentAgencySelections$ =
        this.selectionService.registeredWithEmploymentAgencySelections$;
    receivingPublicAssistanceSelections$ =
        this.selectionService.receivingPublicAssistanceSelections$;
    wioaPartnerProgramSelections$ =
        this.selectionService.wioaPartnerProgramSelections$;
    maritalStatusSelections$ = this.selectionService.maritalStatusSelections$;
    incomePeriodSelections$ = this.selectionService.incomePeriodSelections$;
    immigrationStatusSelections$ =
        this.selectionService.immigrationStatusSelections$;
    hasF1OrJ1VisaSelections$ = this.selectionService.hasF1OrJ1VisaSelections$;
    countryOfOriginSelections$ =
        this.selectionService.countryOfOriginSelections$;
    disabilitySelections$ = this.selectionService.disabilitySelections$;
    disabilityDocumentedSelections$ =
        this.selectionService.disabilityDocumentedSelections$;
    disabilityAccommodationsSelections$ =
        this.selectionService.disabilityAccommodationsSelections$;
    referralSourceSelections$ = this.selectionService.referralSourceSelections$;
    countryOfBirthSelections$ = this.selectionService.countryOfBirthSelections$;
    citizenshipStatusSelections$ =
        this.selectionService.citizenshipStatusSelections$;
    immigrantStatusSelections$ =
        this.selectionService.immigrantStatusSelections$;
    refugeeStatusSelections$ = this.selectionService.refugeeStatusSelections$;
    motherUnder17Selections$ = this.selectionService.motherUnder17Selections$;
    correctionsStatusSelections$ =
        this.selectionService.correctionsStatusSelections$;
    childDependentCareNeededSelections$ =
        this.selectionService.childDependentCareNeededSelections$;
    transportationNeededSelections$ =
        this.selectionService.transportationNeededSelections$;

    EligibilityStatusSelections$ =
        this.selectionService.eligibilityStatusSelections$;

    EligibilityCategorySelections$ =
        this.selectionService.eligibilityCategorySelections$;

    EligibilityConsentSelections$ =
        this.selectionService.eligibilityConsentSelections$;

    disabilityYesNoSelections$ =
        this.selectionService.disabilityYesNoSelections$;

    disabilityAssistanceYesNoSelections$ =
        this.selectionService.disabilityAssistanceYesNoSelections$;

    veteranSelections$ = this.selectionService.veteranSelections$;
    militaryServiceExperienceSelections$ =
        this.selectionService.militaryServiceExperienceSelections$;
    selectiveServiceNotificationSelections$ =
        this.selectionService.selectiveServiceNotificationSelections$;

    client$ = this._client.latest;

    fields$ = this.client$.pipe(
        switchMap((client) => this._client.getClientFields(1))
    );

    /** control for the MatSelect filter keyword */
    private countryOfOriginList: Selection[] = [];
    public filterCountryOfOriginCtrl: FormControl<any> =
        new FormControl<string>('');
    public filteredCountryOfOriginSelections: ReplaySubject<Selection[]> =
        new ReplaySubject<Selection[]>();
    @ViewChild('countryOfOriginSelection', { static: true })
    countryOfOriginSelection!: MatSelect;
    protected _onDestroy = new Subject<void>();

    constructor(
        private readonly _fb: UntypedFormBuilder,
        private readonly snackBar: MatSnackBar,
        private readonly selectionService: SelectionService,
        private readonly _transloco: TranslocoService,
        private readonly _client: ClientService,
        private readonly _googleAnalyticsService: GoogleAnalyticsService
    ) {}

    ngOnInit() {
        // load the initial last school attended list
        this.countryOfOriginSelections$
            .pipe(takeUntil(this._onDestroy))
            .subscribe((selections) => {
                this.countryOfOriginList = selections;
                this.filteredCountryOfOriginSelections.next(selections.slice());
            });

        // listen for search field value changes
        this.filterCountryOfOriginCtrl.valueChanges
            .pipe(takeUntil(this._onDestroy))
            .subscribe(() => {
                this.filterCountryOfOrigin();
            });
    }
    ngAfterViewInit() {
        // Set compareWith function after view is initialized and data is loaded
        this.countryOfOriginSelections$
            .pipe(takeUntil(this._onDestroy), take(1))
            .subscribe(() => {
                if (this.countryOfOriginSelection) {
                    this.countryOfOriginSelection.compareWith = (
                        a: any,
                        b: any
                    ) => {
                        if (a && b) {
                            const aValue =
                                typeof a === 'object' ? a.selectionId : a;
                            const bValue =
                                typeof b === 'object' ? b.selectionId : b;
                            return Number(aValue) === Number(bValue);
                        }
                        return a === b;
                    };
                }
            });
    }

    @Input() submitting: boolean = false;

    @Input()
    set fields(value: ClientField[] | null) {
        if (value) {
            BackgroundForm.setFields(value, this.background);
        }
    }

    @Input() // TODO: NO `any`
    set value(v: any) {
        this.background.patchValue({ ...v });
    }

    @Output()
    submitted = new EventEmitter<any>(); // TODO: NO `any`

    get touched(): boolean {
        return this.background.touched;
    }

    get ethnicitySelectionId(): AbstractControl | null {
        return this.background.get('ethnicitySelectionId');
    }

    get raceSelectionIds(): AbstractControl | null {
        return this.background.get('raceSelectionIds');
    }

    get barrierSelectionIds(): AbstractControl | null {
        return this.background.get('barrierSelectionIds');
    }

    get disabilityYesNoSelectionId(): AbstractControl | null {
        return this.background.get('disabilityYesNoSelectionId');
    }
    get disabilityAssistanceSelectionId(): AbstractControl | null {
        return this.background.get('disabilityAssistanceSelectionId');
    }

    get homelessSite(): AbstractControl | null {
        return this.background.get('homelessSite');
    }

    get laborForceStatusSelectionId(): AbstractControl | null {
        return this.background.get('laborForceStatusSelectionId');
    }

    get workStatusSelectionId(): AbstractControl | null {
        return this.background.get('workStatusSelectionId');
    }

    get employmentStatusSelectionId(): AbstractControl | null {
        return this.background.get('employmentStatusSelectionId');
    }

    get employmentStatusAltSelectionId(): AbstractControl | null {
        return this.background.get('employmentStatusAltSelectionId');
    }

    get employmentStatusTerseSelectionId(): AbstractControl | null {
        return this.background.get('employmentStatusTerseSelectionId');
    }

    get employer(): AbstractControl | null {
        return this.background.get('employer');
    }

    get occupation(): AbstractControl | null {
        return this.background.get('occupation');
    }

    get registeredWithEmploymentAgencySelectionId(): AbstractControl | null {
        return this.background.get('registeredWithEmploymentAgencySelectionId');
    }

    get receivingPublicAssistanceSelectionIds(): AbstractControl | null {
        return this.background.get('receivingPublicAssistanceSelectionIds');
    }

    get wioaPartnerProgramSelectionIds(): AbstractControl | null {
        return this.background.get('wioaPartnerProgramSelectionIds');
    }

    get maritalStatusSelectionId(): AbstractControl | null {
        return this.background.get('maritalStatusSelectionId');
    }

    get familyIncome(): AbstractControl | null {
        return this.background.get('familyIncome');
    }

    get incomePeriodSelectionId(): AbstractControl | null {
        return this.background.get('incomePeriodSelectionId');
    }

    get yourIncome(): AbstractControl | null {
        return this.background.get('yourIncome');
    }

    get immigrationStatusSelectionId(): AbstractControl | null {
        return this.background.get('immigrationStatusSelectionId');
    }

    get hasF1OrJ1VisaSelectionId(): AbstractControl | null {
        return this.background.get('hasF1OrJ1VisaSelectionId');
    }

    get countryOfOriginSelectionId(): AbstractControl | null {
        return this.background.get('countryOfOriginSelectionId');
    }

    get disabilitySelectionId(): AbstractControl | null {
        return this.background.get('disabilitySelectionId');
    }

    get disabilityDocumentedSelectionId(): AbstractControl | null {
        return this.background.get('disabilityDocumentedSelectionId');
    }

    get disabilityAccommodationsSelectionId(): AbstractControl | null {
        return this.background.get('disabilityAccommodationsSelectionId');
    }

    get referralSourceSelectionId(): AbstractControl | null {
        return this.background.get('referralSourceSelectionId');
    }

    get referralOrganizationName(): AbstractControl | null {
        return this.background.get('referralOrganizationName');
    }

    get referralContactName(): AbstractControl | null {
        return this.background.get('referralContactName');
    }

    get referralContactPhone(): AbstractControl | null {
        return this.background.get('referralContactPhone');
    }

    get countryOfBirthSelectionId(): AbstractControl | null {
        return this.background.get('countryOfBirthSelectionId');
    }

    get childrenAges0To5(): AbstractControl | null {
        return this.background.get('childrenAges0To5');
    }

    get childrenAges6To10(): AbstractControl | null {
        return this.background.get('childrenAges6To10');
    }

    get childrenAges11To18(): AbstractControl | null {
        return this.background.get('childrenAges11To18');
    }

    get citizenshipStatusSelectionId(): AbstractControl | null {
        return this.background.get('citizenshipStatusSelectionId');
    }

    get motherUnder17SelectionId(): AbstractControl | null {
        return this.background.get('motherUnder17SelectionId');
    }

    get immigrantStatusSelectionId(): AbstractControl | null {
        return this.background.get('immigrantStatusSelectionId');
    }

    get refugeeStatusSelectionId(): AbstractControl | null {
        return this.background.get('refugeeStatusSelectionId');
    }

    get correctionsStatusSelectionId(): AbstractControl | null {
        return this.background.get('correctionsStatusSelectionId');
    }

    get childDependentCareNeededSelectionId(): AbstractControl | null {
        return this.background.get('childDependentCareNeededSelectionId');
    }

    get transportationNeededSelectionId(): AbstractControl | null {
        return this.background.get('transportationNeededSelectionId');
    }

    get eligibilityStatusSelectionId(): AbstractControl | null {
        return this.background.get('eligibilityStatusSelectionId');
    }

    get eligibilityCategorySelectionId(): AbstractControl | null {
        return this.background.get('eligibilityCategorySelectionId');
    }

    get eligibilityExplanation(): AbstractControl | null {
        return this.background.get('eligibilityExplanation');
    }

    get eligibilityConsentSelectionId(): AbstractControl | null {
        return this.background.get('eligibilityConsentSelectionId');
    }

    get veteranSelectionId(): AbstractControl | null {
        return this.background.get('veteranSelectionId');
    }

    get militaryServiceExperienceSelectionId(): AbstractControl | null {
        return this.background.get('militaryServiceExperienceSelectionId');
    }

    get selectiveServiceNotificationSelectionId(): AbstractControl | null {
        return this.background.get('selectiveServiceNotificationSelectionId');
    }

    onSubmit(event: any): void {
        this.background.markAllAsTouched();
        if (this.background.valid) {
            this.submitting = true;
            this.submitted.next(this.background.value);
        } else {
            // Print control errors if you're unable to submit
            // Object.keys(this.background.controls).forEach(key => {
            //     const control = this.background.get(key);
            //     if (!control?.valid) {
            //         console.log(key);
            //         console.log(control?.errors);
            //     }
            // });
            this._transloco
                .selectTranslate('application.snack.invalid-form')
                .pipe(first())
                .subscribe((message) =>
                    this.snackBar.open(message, undefined, {
                        duration: 4000
                    })
                );

            this._googleAnalyticsService.formError(this.background);
        }
    }

    eligibilityExplanationValidator(): boolean {
        const validationResult = BackgroundForm.eligibilityExplanationValidator(
            this.background
        );
        return validationResult?.eligibilityExplanationRequired ?? false;
    }

    protected filterCountryOfOrigin() {
        let search = this.filterCountryOfOriginCtrl.value;

        if (!search) {
            this.filteredCountryOfOriginSelections.next(
                this.countryOfOriginList.slice()
            );
            return;
        } else {
            search = search.toLowerCase();
        }
        // filter the countries
        this.filteredCountryOfOriginSelections.next(
            this.countryOfOriginList.filter(
                (country) => country.label.toLowerCase().indexOf(search) > -1
            )
        );
    }
}
